// Pure helpers for mystery (placeholder) articles. Framework-free so they
// unit-test like the other helpers/ modules.

import { isEqual } from "lodash";

// Resolve which reveal-line string to show for the current locale.
// Preference: current-locale value, else the other language, else empty.
export const resolveMysteryText = ({ en, fr, language } = {}) =>
  ((language === "fr-CA" ? fr || en : en || fr) || "").trim();

// Assemble a stored mystery item into the article shape every render site,
// download path and PDF expects. `inventory` is intentionally absent so
// getInventoryBadges keeps treating the item as badge-free.
export const mysteryToArticle = (item) => ({
  id: item.id,
  isMystery: true,
  articleName: item.name_en,
  articleNumber: "",
  revealLine: item.revealLine_en || "",
  revealLine_fr: item.revealLine_en || "",
  color: item.color || "",
  color_fr: item.color || "",
  retailPrice: item.retailPrice,
  sizes: item.sizes,
  userFriendlySizes: item.userFriendlySizes,
  displaySizeRange: item.displaySizeRange,
  seasons: item.seasons,
  overallCategoryKey: item.overallCategoryKey,
  imageURLs: [item.imageURL, item.imageURLFull]
});

// Merge mystery items into a Typesense result map. `requestedIDs` bounds the
// output to the ids the caller asked for: every consumer gates its refetch
// on xor(requestedIDs, resolvedIDs), and an EXTRA key loops the effect just
// as hard as a missing one.
export const mergeMysteryArticles = (
  retrievedArticles,
  mysteryArticles,
  requestedIDs
) => {
  if (mysteryArticles == null || Object.keys(mysteryArticles).length === 0) {
    return retrievedArticles;
  }
  const requested = requestedIDs == null ? null : new Set(requestedIDs);
  const merged = { ...retrievedArticles };
  Object.values(mysteryArticles).forEach((item) => {
    if (requested == null || requested.has(item.id)) {
      merged[item.id] = mysteryToArticle(item);
    }
  });
  return merged;
};

// Refresh already-resolved mystery entries in place when a stored item's
// CONTENT changed but the requested id set did not — the id-set case is
// handled by the xor-gated refetch in each reader, which never re-runs on a
// same-id edit. Never adds a key: every reader gates its refetch on
// xor(requestedIDs, resolvedIDs), so a mystery id that isn't already a
// resolved key is skipped rather than inserted (that reader hasn't
// requested it yet — the refetch effect owns adding it). Returns the
// ORIGINAL `resolvedArticles` reference when nothing changed; callers rely
// on that to keep `setState(prev => patchMysteryArticles(prev, ...))` a
// no-op and avoid looping the wiring effects.
export const patchMysteryArticles = (resolvedArticles, mysteryArticles) => {
  if (mysteryArticles == null || Object.keys(mysteryArticles).length === 0) {
    return resolvedArticles;
  }
  let patched = null;
  Object.values(mysteryArticles).forEach((item) => {
    if (!(item.id in resolvedArticles)) return;
    const next = mysteryToArticle(item);
    if (!isEqual(resolvedArticles[item.id], next)) {
      if (patched == null) patched = { ...resolvedArticles };
      patched[item.id] = next;
    }
  });
  return patched == null ? resolvedArticles : patched;
};

// Rep-entered NET (wholesale) default: retailPrice at the "a" price-code
// rate (0.5) or the shared rate every other code (including missing) uses
// (0.6). Bare code strings ("a"/"c"), matching the donor article shape.
export const defaultNetPrice = (retailPrice, priceCode) =>
  Math.round(retailPrice * (priceCode === "a" ? 0.5 : 0.6) * 100) / 100;

// Build a mystery-item draft from a donor article returned by
// searchByArticleNameOrNumber (already carryover-corrected by parseHits).
// The panel needs the donor's id to re-render the template image and to
// seed the image controls, so it is kept in-memory on the draft as
// `templateArticleID`. `mysteryToArticle` still never emits it (or `blur`/
// `greyscale`) to a participant-readable article.
export const draftFromDonor = (donor, itemID) => ({
  id: itemID,
  isMystery: true,
  name_en: "",
  revealLine_en: "Mystery Item",
  revealLine_fr: "",
  color: "TBC",
  retailPrice: donor.retailPrice,
  netPrice: defaultNetPrice(donor.retailPrice, donor.priceCode),
  sizes: [...donor.sizes],
  userFriendlySizes: [...donor.userFriendlySizes],
  displaySizeRange: donor.displaySizeRange,
  seasons: [...donor.seasons],
  overallCategoryKey: donor.overallCategoryKey,
  templateArticleID: donor.id,
  blur: 45,
  greyscale: true,
  imageMode: "obscured"
});

// Shared range formatter for the size-run helpers below.
const computeDisplaySizeRange = (userFriendlySizes) => {
  if (userFriendlySizes.length === 0) return "";
  if (userFriendlySizes.length === 1) return userFriendlySizes[0];
  return `${userFriendlySizes[0]} - ${
    userFriendlySizes[userFriendlySizes.length - 1]
  }`;
};

// Append a new size to the run. Rejects a blank (post-trim) label or a
// case-insensitive duplicate of an existing userFriendlySizes entry by
// returning the draft unchanged (same reference). The new size's `sizes`
// code is the trimmed label itself — a mystery item never maps to a SKU.
export const addSize = (draft, label) => {
  const trimmed = (label || "").trim();
  if (trimmed === "") return draft;
  const isDuplicate = draft.userFriendlySizes.some(
    (ufs) => ufs.toLowerCase() === trimmed.toLowerCase()
  );
  if (isDuplicate) return draft;
  const sizes = [...draft.sizes, trimmed];
  const userFriendlySizes = [...draft.userFriendlySizes, trimmed];
  return {
    ...draft,
    sizes,
    userFriendlySizes,
    displaySizeRange: computeDisplaySizeRange(userFriendlySizes)
  };
};

// Remove one size (matched by its userFriendlySizes label) from the run,
// keeping sizes/userFriendlySizes parallel.
export const removeSize = (draft, label) => {
  const index = draft.userFriendlySizes.indexOf(label);
  if (index === -1) return draft;
  const sizes = draft.sizes.filter((_, i) => i !== index);
  const userFriendlySizes = draft.userFriendlySizes.filter(
    (_, i) => i !== index
  );
  return {
    ...draft,
    sizes,
    userFriendlySizes,
    displaySizeRange: computeDisplaySizeRange(userFriendlySizes)
  };
};

// Re-insert a template size that was previously removed, back at the index
// that keeps template order: immediately after the nearest preceding
// templateSizes entry that is still present in the draft, or at index 0 if
// none precede it. No-ops (returns draft unchanged, same reference) when
// label is already present or isn't a template size at all.
export const restoreSize = (draft, label, templateSizes) => {
  const templates = templateSizes || [];
  const isDuplicate = draft.userFriendlySizes.some(
    (ufs) => ufs.toLowerCase() === label.toLowerCase()
  );
  if (isDuplicate) return draft;
  const templateIndex = templates.indexOf(label);
  if (templateIndex === -1) return draft;

  let insertAt = 0;
  for (let i = templateIndex - 1; i >= 0; i--) {
    const precedingIndex = draft.userFriendlySizes.indexOf(templates[i]);
    if (precedingIndex !== -1) {
      insertAt = precedingIndex + 1;
      break;
    }
  }

  const sizes = [...draft.sizes];
  const userFriendlySizes = [...draft.userFriendlySizes];
  sizes.splice(insertAt, 0, label);
  userFriendlySizes.splice(insertAt, 0, label);

  return {
    ...draft,
    sizes,
    userFriendlySizes,
    displaySizeRange: computeDisplaySizeRange(userFriendlySizes)
  };
};

// dnd-kit arrayMove semantics, implemented locally (no dnd-kit import in a
// pure helper). Out-of-range indices return the draft unchanged.
const moveInArray = (array, fromIndex, toIndex) => {
  const result = array.slice();
  const [moved] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, moved);
  return result;
};

export const reorderSizes = (draft, fromIndex, toIndex) => {
  const length = draft.userFriendlySizes.length;
  const inRange = (index) => index >= 0 && index < length;
  if (!inRange(fromIndex) || !inRange(toIndex)) return draft;
  const sizes = moveInArray(draft.sizes, fromIndex, toIndex);
  const userFriendlySizes = moveInArray(
    draft.userFriendlySizes,
    fromIndex,
    toIndex
  );
  return {
    ...draft,
    sizes,
    userFriendlySizes,
    displaySizeRange: computeDisplaySizeRange(userFriendlySizes)
  };
};

// Blur (0-100, UI units) → sigma as a percentage of source width. Shared
// with the functions repo's blurParamsForWidth so the CSS preview and the
// server render agree on what a given slider position looks like.
export const blurToSigmaPct = (blur) => {
  const clamped = Math.min(Math.max(blur, 0), 100);
  return 0.5 + 4.0 * (clamped / 100);
};

// CSS preview blur radius (px) for a given stage width.
export const previewBlurPx = (blur, stageWidth) =>
  (blurToSigmaPct(blur) / 100) * stageWidth;

// Same URL shape the server's downloadURLForPath builds
// (mysteryImageParams.ts): token-less, ?alt=media, path encoded so
// slashes become %2F. `&v={version}` busts the browser cache after a
// same-path overwrite.
//
// The app persists the imageURLs it builds here (versionedImageURLs is
// written straight into the stored mystery item), so on localhost/LAN QA
// they must point at the Storage emulator or every saved obscured item
// 404s — mirrors services/firebase.js's own emulator-host detection.
const downloadURLForPath = (path) => {
  const { hostname } = window.location;
  const isEmulator = hostname.includes("localhost") || hostname.includes("192.168");
  const host = isEmulator
    ? `http://${hostname}:9198`
    : "https://firebasestorage.googleapis.com";
  return `${host}/v0/b/${
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET
  }/o/${encodeURIComponent(path)}?alt=media`;
};

export const versionedImageURLs = (itemID, version) => ({
  imageURL: `${downloadURLForPath(
    `mysteryItems/${itemID}_MAIN__thumb.jpg`
  )}&v=${version}`,
  imageURLFull: `${downloadURLForPath(
    `mysteryItems/${itemID}_MAIN_.jpg`
  )}&v=${version}`
});

// The one shared "generic placeholder" tile (spec §I): uploaded once by the
// user to both bucket paths below, never re-rendered, so — unlike
// versionedImageURLs — no `&v=` cache-buster is appended.
export const placeholderImageURLs = () => ({
  imageURL: downloadURLForPath("mysteryItems/placeholder_MAIN__thumb.jpg"),
  imageURLFull: downloadURLForPath("mysteryItems/placeholder_MAIN_.jpg")
});

// Shared by every render site that shows a mystery item's thumbnail before
// it is saved (the tray pill, the Set Limits card): a pending obscured-mode
// item has no rendered image on the server yet (its real imageURLs 404), so
// this substitutes the template article's own thumb plus a CSS blur/
// grayscale filter approximating the eventual render, scaled to that site's
// own displayed thumbnail width via previewBlurPx. A pending placeholder-
// mode item already points at the real shared tile, and a saved item (absent
// from pendingMysteryItems) already has its own rendered thumb — both are
// returned unchanged.
export const pendingMysteryPillImage = (article, pendingMysteryItems, stageWidth) => {
  const draft = article?.isMystery ? pendingMysteryItems?.[article.id] : null;
  if (draft == null || draft.imageMode === "placeholder") {
    return { imageURLs: article?.imageURLs, filter: undefined };
  }
  const blurPx = previewBlurPx(draft.blur, stageWidth);
  const filter = `blur(${blurPx}px)${draft.greyscale ? " grayscale(1)" : ""}`;
  return { imageURLs: draft.templateImageURLs, filter };
};
